import { CheckCircleFilled } from '@ant-design/icons';
import { Button, Flex } from 'antd';
import { ReactComponent as SuccessDossierIcon } from 'assets/icons/SuccessDossier.svg';
import { MultiStepModal, WizardStep } from 'components/MultiStepModal';
import { useTranslation } from 'react-i18next';
import CloseConfirmationModal from 'views/dossiers/ajout/components/CloseConfirmationModal';
import MinimizedDraftButton from 'views/dossiers/ajout/components/MinimizedDraftButton';
import BeneficiaryForm from 'views/dossiers/ajout/forms/Beneficiary.form';
import ProjectInformationsForm from 'views/dossiers/ajout/forms/ProjectInformations.form';
import SiteInformationsForm from 'views/dossiers/ajout/forms/SiteInformations.form';
import useAjoutDossierFormController from 'views/dossiers/ajout/hooks/useAjoutDossierFormController';

interface AjoutDossierFormProps {
  openRequestId: number;
  onSuccess?: () => void;
}

export default function AjoutDossierForm({
  openRequestId,
  onSuccess,
}: AjoutDossierFormProps) {
  const { t } = useTranslation(['ajout_dossier', 'global']);

  const { state, ui, forms, mutations, actions } =
    useAjoutDossierFormController({
      openRequestId,
      onSuccess,
    });

  if (!state.isHydrated) {
    return null;
  }

  const requiredFieldsText = t('global:champs_requis');
  const [requiredTextBefore, requiredTextAfter] = requiredFieldsText.split('*');

  const stepItems: WizardStep[] = [
    {
      id: 'site',
      title: t('ajout_dossier:etapes:etape_1_short'),
      next: {
        enabled: state.isSiteStepComplete,
      },
      content: (
        <>
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-1">
              {t('ajout_dossier:etapes:etape_1_short')}
            </h2>
            <p className="text-sm text-gray-500">
              {requiredTextBefore}
              <span className="text-rouge-copro-6">*</span>
              {requiredTextAfter}
            </p>
          </div>

          <SiteInformationsForm
            key={`site-form-${state.isDraftActive ? 'active' : 'inactive'}-${state.siteFormUiResetKey}`}
            contactFormInstance={forms.siteContactFormInstance}
            addressFormInstance={forms.siteAddressFormInstance}
            onCompletionChange={actions.handleSiteCompletionChange}
            initialIsComplete={state.isSiteStepComplete}
            onValuesChange={actions.handleSiteValuesChange}
          />
        </>
      ),
    },
    {
      id: 'project',
      title: t('ajout_dossier:etapes:etape_2_short'),
      next: {
        enabled: state.isProjectStepSubmittable,
        onClick: actions.validateProjectStep,
      },
      content: (
        <>
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-1">
              {t('ajout_dossier:etapes:etape_2_short')}
            </h2>
            <p className="text-sm text-gray-500">
              {requiredTextBefore}
              <span className="text-rouge-copro-6">*</span>
              {requiredTextAfter}
            </p>
          </div>
          <ProjectInformationsForm
            formInstance={forms.projectFormInstance}
            onValuesChange={actions.handleProjectValuesChange}
          />
        </>
      ),
    },
    {
      id: 'beneficiary',
      title: t('ajout_dossier:etapes:etape_3_short'),
      next: {
        enabled: state.isBeneficiaryStepSubmittable,
      },
      content: (
        <>
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-1">
              {t('ajout_dossier:etapes:etape_3_short')}
            </h2>
            <p className="text-sm text-gray-500">
              {requiredTextBefore}
              <span className="text-rouge-copro-6">*</span>
              {requiredTextAfter}
            </p>
          </div>
          <BeneficiaryForm
            formInstance={forms.beneficiaryFormInstance}
            siteContactInfo={ui.siteContactInfo}
            onValuesChange={actions.handleBeneficiaryValuesChange}
          />
        </>
      ),
    },
  ];

  /* Succès */
  return (
    <>
      <MultiStepModal
        open={state.isDraftActive && !state.isDraftMinimized}
        steps={stepItems}
        currentStep={state.currentSlide}
        onStepChange={actions.handleStepChange}
        onRequestClose={actions.handleCloseRequest}
        onRequestSuccessClose={actions.handleCloseSuccessModal}
        onRequestMinimize={actions.requestMinimize}
        isMinimizing={ui.isMinimizing}
        destroyOnClose={false}
        wrapClassName="ajout-dossier-form-wrap"
        submit={{
          mutation: mutations.createDossierMutation,
          getVariables: actions.buildCreateDossierPayload,
          onSuccess: actions.handleCreateDossierSuccess,
        }}
        success={({ closeSuccess }) => (
          <Flex
            vertical
            align="center"
            justify="center"
            className="text-center py-12 px-8"
          >
            <SuccessDossierIcon className="mb-6" />
            <div className="bg-vert-transport-1 rounded-lg px-6 py-4 mb-6 max-w-md">
              <p className="flex items-center justify-start gap-2 font-semibold mb-1 text-vert-transport-10">
                <CheckCircleFilled className="text-vert-transport-10" />
                {t('ajout_dossier:success:title')}
              </p>
              <p className="text-left text-sm text-gray-600 m-0 ml-6">
                {t('ajout_dossier:success:text')}
              </p>
            </div>

            <Button
              type="primary"
              shape="round"
              size="large"
              onClick={closeSuccess}
            >
              {t('global:back_home')}
            </Button>
          </Flex>
        )}
      />

      {state.isDraftActive && state.isDraftMinimized && (
        <MinimizedDraftButton onClick={actions.expandDraft} />
      )}

      <div
        ref={ui.minimizeTargetRef}
        aria-hidden="true"
        className="fixed bottom-0 right-4 h-0 w-0"
      />

      <CloseConfirmationModal
        open={state.isCloseConfirmationOpen}
        onCancel={actions.handleCancelCloseRequest}
        onConfirm={actions.handleConfirmClose}
      />
    </>
  );
}
